import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface OpenPOItem {
  supplier_name: string;
  supplier_code: string;
  po_number: string;
  posting_date: string;
  creation_date: string;
  date_of_update: string;
  part_number: string;
  description: string;
  outstanding_qty: number;
  in_stock: number | null;
  price: number | null;
  quantity: number;
  delivery_date: string;
  warehouse_code: string;
  quantity2: number | null;
  volume_uom: string;
  length1: number | null;
  width1: number | null;
  height1: number | null;
  group_name: string;
  poland_stock: number | null;
  [key: string]: unknown;
}

interface OpenPOSubTabProps {
  partNumber?: string;
}

export default function OpenPOSubTab({ partNumber }: OpenPOSubTabProps) {
  const { formatDate } = useFormatters();
  const [items, setItems] = useState<OpenPOItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getOpenPO(params);
      const data = res.data as { results?: OpenPOItem[]; items?: OpenPOItem[]; count?: number; total_count?: number };
      setItems(data.results || data.items || []);
      setTotalCount(data.count || data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load open PO data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        placeholder="Search by part number or PO number…"
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No open PO data available.</p>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-2 font-medium whitespace-nowrap">Customer/Supplier Name</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Customer/Supplier No.</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Document Number</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Posting Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Creation Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Date of Update</th>
              <th
                className="text-left p-2 font-medium whitespace-nowrap cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Item No. {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Item/Service Description</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Remaining Open Quantity</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">In Stock</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Price</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Quantity</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Row Delivery Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse Code</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Quantity2</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Volume UoM</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Length 1</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Width 1</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Height 1</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Group Name</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Poland Stock</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b hover:bg-muted/30">
                <td className="p-2 whitespace-nowrap">{item.supplier_name}</td>
                <td className="p-2 whitespace-nowrap">{item.supplier_code}</td>
                <td className="p-2 whitespace-nowrap">{item.po_number}</td>
                <td className="p-2 whitespace-nowrap">{item.posting_date}</td>
                <td className="p-2 whitespace-nowrap">{item.creation_date}</td>
                <td className="p-2 whitespace-nowrap">{item.date_of_update}</td>
                <td className="p-2 font-mono text-xs whitespace-nowrap">{item.part_number}</td>
                <td className="p-2">{item.description}</td>
                <td className="p-2 text-right">{item.outstanding_qty}</td>
                <td className="p-2 text-right">{item.in_stock ?? ''}</td>
                <td className="p-2 text-right">{item.price ?? ''}</td>
                <td className="p-2 text-right">{item.quantity}</td>
                <td className="p-2 whitespace-nowrap">{item.delivery_date ? formatDate(item.delivery_date) : ''}</td>
                <td className="p-2 whitespace-nowrap">{item.warehouse_code}</td>
                <td className="p-2 text-right">{item.quantity2 ?? ''}</td>
                <td className="p-2">{item.volume_uom}</td>
                <td className="p-2 text-right">{item.length1 ?? ''}</td>
                <td className="p-2 text-right">{item.width1 ?? ''}</td>
                <td className="p-2 text-right">{item.height1 ?? ''}</td>
                <td className="p-2">{item.group_name}</td>
                <td className="p-2 text-right">{item.poland_stock ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
